<template>
  <div class="pagination-wrapper">
    <div class="per-page">
      <div>{{ t('____x.rowsperpage') }}:</div>
      <ui-select
        class="select-rows-per-page pl-8"
        v-model="rowsPerPageModel"
        :items="rowsPerPageOptions"
        :hide-details="1"
        :autocomplete="false"
      />
    </div>
    <div class="ml-8">{{ pageStart }}-{{ pageEnd }} of {{ itemsCount }}</div>
    <div class="btn-wrapper">
      <ui-button
        :color="colors.white"
        button-style="outline"
        icon="fas fa-chevron-left"
        class="pagination-page-button ml-8"
        :disabled="props.page <= 1"
        @click="emit('update:page', props.page - 1)"
      />
      <ui-button
        icon="fas fa-chevron-right"
        class="pagination-page-button ml-8"
        :disabled="pageEnd === itemsCount"
        button-style="outline"
        @click="emit('update:page', props.page + 1)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n, useColors } from '@/composables';

const props = defineProps<{
  itemsCount: number;
  page: number;
  rowsPerPageCount: number | string;
}>();

const emit = defineEmits<{
  'update:page': [nextPage: number];
  'update:rowsPerPageCount': [newRowsPerPage: number | string];
}>();

const { t } = useI18n();
const colors = useColors();

const rowsPerPageOptions = computed(() => [
  { text: '5', value: 5 },
  { text: '10', value: 10 },
  { text: '20', value: 20 },
  { text: t('All'), value: 'all' },
]);

const rowsPerPageModel = computed({
  get: () => {
    const raw = props.rowsPerPageCount;
    if (raw === 'all' || raw === 'All') {
      return 'all';
    }
    const asNumber = Number(raw);
    return Number.isNaN(asNumber) ? raw : asNumber;
  },
  set: (nextRowsPerPage: number | string) => {
    const normalized =
      nextRowsPerPage === 'all' || nextRowsPerPage === 'All'
        ? 'all'
        : Number(nextRowsPerPage);
    emit('update:rowsPerPageCount', normalized);
    emit('update:page', 1);
  },
});

const pageStart = computed(() => {
  if (props.rowsPerPageCount === 'all') {
    return 1;
  }
  const rowsPerPage = props.rowsPerPageCount as number;
  return Math.max((props.page - 1) * rowsPerPage + 1, 1);
});

const pageEnd = computed(() => {
  if (props.rowsPerPageCount === 'all') {
    return props.itemsCount;
  }
  const rowsPerPage = props.rowsPerPageCount as number;
  return Math.min(props.page * rowsPerPage, props.itemsCount);
});
</script>

<style lang="scss" scoped>
.pagination-wrapper {
  display: flex;
  justify-content: end;
  align-items: center;
  height: 50px;
  color: var(--color-gray50);
}

.btn-wrapper {
  display: flex;
}

.per-page {
  display: flex;
  align-items: center;
}

.pagination-page-button {
  :deep(.ui-button) {
    height: 28px;
    min-width: 28px;
    background-color: transparent !important;
    border: 1px solid var(--color-gray20) !important;
    justify-content: center;
    padding: 0 !important;

    .icon {
      font-size: 15px !important;
    }
  }

  :deep(.ui-button__main) {
    flex: none;
  }
}

.select-rows-per-page {
  min-width: 60px;
  width: max-content;
  flex-shrink: 0;

  :deep(.v-field__input),
  :deep(.v-select__selections) {
    min-width: min-content;
    overflow: visible;
  }

  :deep(.v-select__selection),
  :deep(.v-autocomplete__selection) {
    overflow: visible;
    text-overflow: clip;
  }

  :deep(.v-input__slot),
  :deep(.v-select__slot input),
  :deep(.v-field input) {
    cursor: pointer !important;
  }

  :deep(.v-field__append-inner .v-icon),
  :deep(.v-input__icon--append .v-icon) {
    font-size: 12px !important;
    line-height: 11px !important;
  }
}
</style>

<style lang="scss">
.pagination-wrapper .v-text-field input,
.pagination-wrapper .v-field input {
  border: none !important;
  background: none !important;
}
</style>
